import { Numbers, isNotNullish } from '@nx/stdlib';

import type { DiscountResponse, UsageBreakdownItemResponse, UsageResponse } from '../console.api.types';
import { CONSUMPTION_TYPE, CURRENCY } from '../console.types';
import type { Consumption, ConsumptionBreakdownItem, Usage, UsageBreakdownItem, UsageDiscount } from '../console.types';

const transformDiscountResponse = (response: DiscountResponse): UsageDiscount => ({
  promotionName: response.promotion_name,
  voucherCode: response.voucher_code,
  dollarAmountOff: response.dollar_amount_off,
});

const transformBreakdownResponse = (response: UsageBreakdownItemResponse): UsageBreakdownItem => ({
  dbid: response.dbid,
  name: response.name,
  consumptionUnits: response.consumption_units,
  unitType: response.unit_type,
  unitCostInDollars: response.unit_cost_in_dollars,
  cost: response.cost,
  costType: response.cost_type,
  billingCategory: response.billing_category,
  endTime: response.end_time,
  tier: response.tier,
  pricingPlan: response.pricing_plan,
  productName: response.product_name,
});

const transformCurrency = (currency: string): CURRENCY => {
  if (currency === CURRENCY.DOLLARS.toString()) {
    return CURRENCY.DOLLARS;
  }

  if (currency === CURRENCY.CREDITS.toString()) {
    return CURRENCY.CREDITS;
  }

  throw new Error(`Invalid currency value in usage reports: ${currency}`);
};

export const transformUsageResponse = (response: UsageResponse): Usage => ({
  totalCost: response.total_cost,
  totalCostType: transformCurrency(response.total_cost_type),
  totalBreakdownCount: response.total_breakdown_count,
  discount: isNotNullish(response.discount) ? transformDiscountResponse(response.discount) : undefined,
  breakdown: response.breakdown?.map((breakdown) => transformBreakdownResponse(breakdown)),
  hasPricingPlanChange: response.has_pricing_plan_change,
  usagePeriodEnd: response.usage_period_end,
  usagePeriodStart: response.usage_period_start,
});

export const transformBreakdownResponseToConsumptionBreakdown = (
  response: UsageBreakdownItemResponse,
): ConsumptionBreakdownItem => ({
  dbid: response.dbid,
  name: response.name,
  tier: response.tier,
  productName: response.product_name,
  pricingPlan: response.pricing_plan,
  usage: Numbers.formatToDecimalString(response.consumption_units),
  cost: response.cost,
  currency: transformCurrency(response.cost_type),
  unitOfMeasure: response.unit_type,
  unitCostInDollars: response.unit_cost_in_dollars,
  billingStatus: response.end_time ? 'Ended' : 'Ongoing',
  consumptionType: CONSUMPTION_TYPE.DATABASE,
});

export const transformUsageResponseToConsumption = (response: UsageResponse): Consumption => ({
  totalCost: response.total_cost,
  currency: transformCurrency(response.total_cost_type),
  totalBreakdownCount: response.total_breakdown_count,
  discount: isNotNullish(response.discount) ? transformDiscountResponse(response.discount) : undefined,
  breakdown: response.breakdown?.map((breakdown) => transformBreakdownResponseToConsumptionBreakdown(breakdown)),
  usagePeriodEnd: response.usage_period_end,
  usagePeriodStart: response.usage_period_start,
});
